// react imports
import axios from "axios";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Breadcrumb, Layout, Menu } from "antd";
import { RootState } from "app/store";
import { clearAuthData } from "features/auth/authSlice";

import Category from "./Category";

// common imports
import CalculatorIcon from "common/svg/CalculatorIcon";
import ClipboardIcon from "common/svg/ClipboardIcon";
import ExitIcon from "common/svg/ExitIcon";
import UsersIcon from "common/svg/UsersIcon";
import LogoColor from "common/svg/LogoColor";
import { API_URL } from "common/constants/config";

// styled-components
import Container from "./styled-components/Container";
import EmailIndicator from "./styled-components/EmailIndicator";
import LogotypeContainer from "./styled-components/LogotypeContainer";
import LogOutButton from "./styled-components/LogOutButton";

import "antd/dist/antd.css";
import "./css/Breadcrumb.css";
import "./css/Header.css";
import "./css/Sider.css";
import "./css/Layout.css";
import { BREADCRUMBS } from "./constants/breadcrumbs";

export type CategoryName =
  | "users"
  | "results"
  | "residues"
  | "electricity"
  | "energies"
  | "water"
  | "aviation"
  | "publicTransport"
  | "vehicles";

const { Content, Header, Sider } = Layout;
const { SubMenu } = Menu;

function Admin() {
  const dispatch = useDispatch();
  const history = useHistory();
  const [category, setCategory] = useState<CategoryName>("users");
  const emailSelector = useSelector((state: RootState) => state.auth.email);
  if (!emailSelector) history.replace("/");

  function handleCategorySelection(selected: CategoryName) {
    return () => setCategory(selected);
  }

  async function signOut() {
    await axios.post("/auth/sign-out", null, { baseURL: API_URL });
    dispatch(clearAuthData());
  }

  return (
    <Container>
      <Layout>
        <Header>
          <LogotypeContainer>
            <LogoColor size={104} />
          </LogotypeContainer>

          <EmailIndicator>{emailSelector}</EmailIndicator>
          <LogOutButton onClick={signOut}>
            <ExitIcon />
          </LogOutButton>
        </Header>
        <Layout>
          <Sider width={200}>
            <Menu
              mode="inline"
              style={{ width: "100%", borderRight: 0 }}
              inlineIndent={16}
              selectedKeys={[category]}
            >
              <Menu.Item
                key="users"
                icon={<UsersIcon />}
                onClick={handleCategorySelection("users")}
              >
                Usuarios
              </Menu.Item>
              <Menu.Item
                key="results"
                icon={<ClipboardIcon />}
                onClick={handleCategorySelection("results")}
              >
                Results
              </Menu.Item>
              <SubMenu
                key="calculations"
                title="Calculos"
                icon={<CalculatorIcon />}
              >
                <Menu.Item
                  key="residues"
                  onClick={handleCategorySelection("residues")}
                >
                  Residuos
                </Menu.Item>
                <Menu.Item
                  key="electricity"
                  onClick={handleCategorySelection("electricity")}
                >
                  Electricidad
                </Menu.Item>
                <Menu.Item
                  key="energies"
                  onClick={handleCategorySelection("energies")}
                >
                  Energías
                </Menu.Item>
                <Menu.Item
                  key="water"
                  onClick={handleCategorySelection("water")}
                >
                  Agua
                </Menu.Item>
                <Menu.Item
                  key="aviation"
                  onClick={handleCategorySelection("aviation")}
                >
                  Aviación
                </Menu.Item>
                <Menu.Item
                  key="publicTransport"
                  onClick={handleCategorySelection("publicTransport")}
                >
                  Transporte público
                </Menu.Item>
                <Menu.Item
                  key="vehicles"
                  onClick={handleCategorySelection("vehicles")}
                >
                  Vehículos
                </Menu.Item>
              </SubMenu>
            </Menu>
          </Sider>
          <Layout>
            <Breadcrumb>
              {BREADCRUMBS[category].map((item) => (
                <Breadcrumb.Item key={item}>{item}</Breadcrumb.Item>
              ))}
            </Breadcrumb>
            <Content>
              <Category category={category} />
            </Content>
          </Layout>
        </Layout>
      </Layout>
    </Container>
  );
}

export default Admin;
